import React, { useMemo, useState } from "react";
import { useQuery } from "@tanstack/react-query";
import { Phone, Delete, Search, User } from "lucide-react";
import { fetchContacts } from "@/lib/api";
import { isCallableNumber } from "@/lib/call";
import { initials } from "@/lib/format";
import { usePlaceCall, ConfirmCallDialog } from "@/components/CallFlow";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription,
} from "@/components/ui/dialog";

const KEYS = ["1", "2", "3", "4", "5", "6", "7", "8", "9", "*", "0", "#"];

// Dial pad sheet: keypad + paste-friendly field + contact search, then the
// standard confirmation flow. Used by the desktop "Appeler" button and the
// mobile floating button.
export const Dialer = ({ trigger }) => {
  const [open, setOpen] = useState(false);
  const [number, setNumber] = useState("");
  const [query, setQuery] = useState("");
  const [pickedName, setPickedName] = useState(null);
  const [confirmOpen, setConfirmOpen] = useState(false);
  const { launch, pending, cooldown } = usePlaceCall();

  const { data: contacts = [] } = useQuery({
    queryKey: ["contacts"],
    queryFn: () => fetchContacts(),
    enabled: open,
  });

  const matches = useMemo(() => {
    const q = query.trim().toLowerCase();
    if (!q) return [];
    const digits = q.replace(/\D/g, "");
    return contacts
      .filter((c) =>
        (c.name && c.name.toLowerCase().includes(q)) ||
        (digits && (c.number_key || "").includes(digits)))
      .slice(0, 5);
  }, [contacts, query]);

  const callable = isCallableNumber(number);
  const openSheet = () => {
    setNumber("");
    setQuery("");
    setPickedName(null);
    setOpen(true);
  };

  const pickContact = (c) => {
    setNumber(c.number || "");
    setPickedName(c.has_name ? c.name : null);
    setQuery("");
  };

  const triggerEl = trigger
    ? React.cloneElement(trigger, { onClick: openSheet })
    : (
      <button
        data-testid="open-dialer-btn"
        onClick={openSheet}
        className="w-full flex items-center justify-center gap-2 rounded-lg bg-emerald-600 text-white text-sm font-semibold py-2.5 hover:bg-emerald-700 transition-colors focus:outline-none focus:ring-2 focus:ring-emerald-500 focus:ring-offset-2"
      >
        <Phone className="h-4 w-4" strokeWidth={2.4} />
        Appeler
      </button>
    );

  return (
    <>
      {triggerEl}
      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent className="sm:max-w-[340px]" data-testid="dialer-sheet">
          <DialogHeader>
            <DialogTitle className="font-heading">Nouvel appel</DialogTitle>
            <DialogDescription className="sr-only">Composer un numéro ou rechercher un contact</DialogDescription>
          </DialogHeader>

          <div className="relative">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-slate-400" />
            <input
              data-testid="dialer-search-input"
              value={query}
              onChange={(e) => setQuery(e.target.value)}
              placeholder="Rechercher un contact…"
              className="w-full rounded-lg border border-slate-200 bg-white pl-9 pr-3 py-2.5 text-sm focus:outline-none focus:ring-2 focus:ring-emerald-500"
            />
          </div>

          {matches.length > 0 && (
            <div className="rounded-lg border border-slate-200 divide-y divide-slate-100 overflow-hidden" data-testid="dialer-contact-results">
              {matches.map((c) => (
                <button
                  key={c.number_key}
                  type="button"
                  data-testid={`dialer-contact-${c.number_key}`}
                  onClick={() => pickContact(c)}
                  className="w-full flex items-center gap-2.5 px-3 py-2 text-left hover:bg-slate-50 active:bg-slate-100 transition-colors"
                >
                  <span className="h-7 w-7 rounded-full bg-slate-100 border border-slate-200 flex items-center justify-center shrink-0">
                    {c.has_name
                      ? <span className="text-[10px] font-semibold text-slate-500">{initials(c.name)}</span>
                      : <User className="h-3.5 w-3.5 text-slate-400" />}
                  </span>
                  <span className="min-w-0">
                    <span className="block text-sm font-medium text-slate-900 truncate">{c.name}</span>
                    <span className="block text-xs text-slate-400 tabular-nums">{c.number}</span>
                  </span>
                </button>
              ))}
            </div>
          )}

          <input
            data-testid="dialer-number-input"
            type="tel"
            value={number}
            onChange={(e) => { setNumber(e.target.value); setPickedName(null); }}
            placeholder="06 12 34 56 78"
            className="w-full rounded-lg border border-slate-200 bg-white px-3 py-3 text-center text-lg font-semibold tabular-nums tracking-wide focus:outline-none focus:ring-2 focus:ring-emerald-500"
          />

          <div className="grid grid-cols-3 gap-2" data-testid="dialer-keypad">
            {KEYS.map((k) => (
              <button
                key={k}
                type="button"
                data-testid={`dialer-key-${k === "*" ? "star" : k === "#" ? "hash" : k}`}
                onClick={() => setNumber((n) => n + k)}
                className="h-12 rounded-xl border border-slate-200 bg-white text-lg font-semibold text-slate-800 hover:border-emerald-300 hover:text-emerald-700 active:bg-emerald-50 transition-colors"
              >
                {k}
              </button>
            ))}
          </div>

          {number && !callable && (
            <p data-testid="dialer-not-allowed-hint" className="text-xs text-slate-400 text-center">
              Numéro non autorisé pour les appels sortants
            </p>
          )}

          <div className="flex gap-2">
            <button
              type="button"
              data-testid="dialer-backspace"
              onClick={() => setNumber((n) => n.slice(0, -1))}
              aria-label="Effacer"
              className="h-11 w-14 rounded-lg border border-slate-200 bg-white text-slate-500 flex items-center justify-center hover:bg-slate-50 transition-colors"
            >
              <Delete className="h-4 w-4" />
            </button>
            <button
              type="button"
              data-testid="dialer-call-btn"
              onClick={() => setConfirmOpen(true)}
              disabled={!callable || pending || cooldown}
              className="flex-1 inline-flex items-center justify-center gap-2 rounded-lg bg-emerald-600 text-white text-sm font-semibold py-3 hover:bg-emerald-700 disabled:opacity-50 transition-colors"
            >
              <Phone className="h-4 w-4" /> Appeler
            </button>
          </div>
        </DialogContent>
      </Dialog>

      <ConfirmCallDialog
        open={confirmOpen}
        onOpenChange={setConfirmOpen}
        number={number}
        name={pickedName}
        pending={pending}
        onLaunch={async () => {
          const ok = await launch(number, pickedName);
          if (ok) {
            setConfirmOpen(false);
            setOpen(false);
          }
        }}
      />
    </>
  );
};
